import React, { useState } from 'react';
import { useTranslate } from '@hooks';
import { localeNames } from '@dotone/react-ui-core/constants';
import classnames from 'classnames';
import { UncontrolledTooltip } from 'reactstrap';
import { getNameWithId, numberToCurrency } from '@dotone/react-ui-core/utils';
import {
  DateTime,
  FileStatusBadge,
  Currency,
  TimePeriod,
  OfferVariantStatusBadge,
  ConversionTypeBadge,
  Text,
  CollapsedTextareaClipboard,
  ImageCircle,
  FlexContainer,
  StarRating,
  SiteInfoCollapsibleList,
  ImageBrand,
  AffiliateBioPopover,
} from '@dotone/react-ui-core';
import AffiliateDetailModal from './Affiliates/AffiliateDetailModal';

export const formatCreativeTimePeriod = (creative, props) =>
  creative ? (
    <TimePeriod
      startDate={creative.activeDateStart}
      endDate={creative.activeDateEnd}
      ongoing={creative.ongoing}
      isInfinity={creative.isInfinityTime}
      {...props}
    />
  ) : null;

export const formatFileStatus = (cell) => <FileStatusBadge block value={cell} />;

export const formatDateTime = (cell, props) =>
  cell && <DateTime value={cell} className="text-nowrap" {...props} />;

export const formatCurrency = (cell, props) => <Currency round={false} value={cell} {...props} />;

const AffiliateAvatar = ({ affiliate, avatarWidth, className, ...props }) => {
  const { t } = useTranslate('affiliate', 'hints');
  const [isOpen, setIsOpen] = useState(false);
  const onToggle = () => setIsOpen(!isOpen);
  const avatarId = `affiliate-avatar-${affiliate.id}`;

  const avatar = (
    <ImageCircle
      avatar
      id={avatarId}
      src={affiliate.avatarCdnUrl}
      width={avatarWidth || 50}
      className={classnames('border', className, {
        'border-warning avatar-ring': affiliate.direct,
        'border-light': !affiliate.direct,
      })}
      {...props}
    />
  );

  const tooltip = affiliate.direct && (
    <UncontrolledTooltip target={avatarId}>{t('sourceTypeDirectAp.content')}</UncontrolledTooltip>
  );

  if (affiliate.canAccess) {
    return (
      <>
        <a onClick={onToggle}>{avatar}</a>
        {tooltip}
        <AffiliateDetailModal affiliate={affiliate} onCancel={onToggle} isOpen={isOpen} />
      </>
    );
  }

  return (
    <>
      {avatar}
      {tooltip}
    </>
  );
};

export const formatAvatar = (affiliate, props = {}) => (
  <AffiliateAvatar affiliate={affiliate} {...props} />
);

export const formatOfferStatus = (cell) => cell && <OfferVariantStatusBadge value={cell} />;

export const formatConvType = (cell) => cell && <ConversionTypeBadge value={cell} />;

export const formatDaysReturn = (cell, { order }, t) => {
  if (!order) return null;

  const { daysSinceOrder, daysReturnPastDue } = order;

  return daysReturnPastDue ? t('confirmOrder') : cell - daysSinceOrder;
};

export const formatCreativeLocales = (cell) =>
  cell?.map((locale) => (
    <Text key={locale} nowrap>
      {localeNames[String(locale)]}
    </Text>
  ));

export const formatTextareaClipboard = (cell) =>
  cell && <CollapsedTextareaClipboard value={cell} />;

export const formatAffiliate = (affiliate, { minimal = false, ...otherProps } = {}) => {
  const name = affiliate.canAccess ? getNameWithId(affiliate) : affiliate.id;

  return (
    <FlexContainer column justify="center" align="center" className="text-center">
      <div className="my-1">{formatAvatar(affiliate, otherProps)}</div>
      <div className="my-1">{name}</div>
      {!minimal && affiliate.email && <div className="my-1 break-word">{affiliate.email}</div>}
      {!minimal && <AffiliateBioPopover bio={affiliate.bio} target={`bio-${affiliate.id}`} />}
    </FlexContainer>
  );
};

export const formatRating = (cell, affiliate) => (
  <span className={!affiliate.canAccess ? 'bg-blur' : undefined}>
    <StarRating value={cell} />
  </span>
);

export const formatSiteInfos = (cell, affiliate) => {
  const siteInfos = cell.filter((siteInfo) => !!siteInfo.mediaCategory);
  return <SiteInfoCollapsibleList items={siteInfos} isPro={affiliate.canAccess} />;
};

export const formatEarningRating = (cell, affiliate, t) => {
  let value;
  if (affiliate.canAccess && cell) {
    value = `${numberToCurrency(cell, { truncate: true })}+`;
  } else if (cell) {
    value = cell;
  } else {
    value = t('common:na');
  }

  return <div className={classnames({ 'bg-blur': !affiliate.canAccess })}>{value}</div>;
};

export const formatCampaignMetrics = (cell, affiliate) => (
  <div className={classnames({ 'bg-blur': !affiliate.canAccess })}>{cell.toString()}</div>
);

export const formatOffer = (cell, row, targetId) => {
  const id = targetId || (row && `offer-${row.id}`);

  return (
    <>
      <ImageBrand id={id} src={cell} size="sm" className="mb-1 mx-auto" />
      {id && row && <UncontrolledTooltip target={id}>{getNameWithId(row)}</UncontrolledTooltip>}
    </>
  );
};
